import { BgColorOptions, ButtonsOptions } from '@df/ui/atomic/atomic-options';
import { NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { NCheckoutAccountNewCms } from './checkout-account-new-cms.interface';

export const CHECKOUT_ACCOUNT_NEW_CMS_CONFIG: Omit<NICmsContentComponent.IMeta<NICmsContentComponent.IComponent>, 'type'> = {
    id: NCheckoutAccountNewCms.BLOCK_ID,
    block: {
        url: NCheckoutAccountNewCms.BLOCK_ID
    },
    onDemandOnly: true,
    name: 'Checkout Account Content Block',
    description: 'Content block for the checkout account step with configurable text and block ordering',
    fields: [
        // Block ordering configuration
        <NICmsContentField.IRepeaterField>{
            key: 'blocks',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Blocks Configuration',
            itemName: 'Block',
            itemNameField: 'type',
            complex: true,
            defaultSize: 4,
            fields: [
                <NICmsContentField.IChoiceField>{
                    key: 'type',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Block Type',
                    required: true,
                    options: [
                        { key: 'guest_checkout', title: 'Guest Checkout' },
                        { key: 'sign_in', title: 'Sign In' },
                        { key: 'create_account', title: 'Create Account' },
                        { key: 'separator', title: 'Separator' }
                    ]
                },
                <NICmsContentField.IField>{
                    key: '_sectionDefault',
                    type: NICmsContentField.EFieldType.label,
                    title: 'Default State',
                    canShow: (data: NCheckoutAccountNewCms.IBlock) => data?.type !== 'separator'
                },
                <NICmsContentField.ITextField>{
                    key: 'title',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Title',
                    required: true,
                    default: 'Guest Checkout',
                    canShow: (data: NCheckoutAccountNewCms.IBlock) => data?.type !== 'separator'
                },
                <NICmsContentField.ITextField>{
                    key: 'copy',
                    type: NICmsContentField.EFieldType.textarea,
                    title: 'Copy',
                    canShow: (data: NCheckoutAccountNewCms.IBlock) => data?.type !== 'separator'
                },
                <NICmsContentField.ITextField>{
                    key: 'ctaText',
                    type: NICmsContentField.EFieldType.text,
                    title: 'CTA Text',
                    required: true,
                    default: 'Continue',
                    canShow: (data: NCheckoutAccountNewCms.IBlock) => data?.type !== 'separator'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'ctaStyle',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'CTA Style',
                    options: ButtonsOptions,
                    default: ButtonsOptions[1] ? ButtonsOptions[1].key : undefined,
                    canShow: (data: NCheckoutAccountNewCms.IBlock) => data?.type !== 'separator'
                },
                <NICmsContentField.IField>{
                    key: '_sectionSelected',
                    type: NICmsContentField.EFieldType.label,
                    title: 'Selected State',
                    canShow: (data: NCheckoutAccountNewCms.IBlock) => data?.type !== 'separator'
                },
                <NICmsContentField.ITextField>{
                    key: 'selectedTitle',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Title',
                    description: 'Title to display when the block is selected',
                    canShow: (data: NCheckoutAccountNewCms.IBlock) => data?.type !== 'separator'
                },
                <NICmsContentField.ITextField>{
                    key: 'selectedCopy',
                    type: NICmsContentField.EFieldType.textarea,
                    title: 'Copy',
                    description: 'Copy to display when the block is selected',
                    canShow: (data: NCheckoutAccountNewCms.IBlock) => data?.type !== 'separator'
                },
                <NICmsContentField.ITextField>{
                    key: 'selectedCtaText',
                    type: NICmsContentField.EFieldType.text,
                    title: 'CTA Text',
                    description: 'Text to display when the block is selected',
                    canShow: (data: NCheckoutAccountNewCms.IBlock) => data?.type !== 'separator'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'selectedCtaStyle',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'CTA Style',
                    options: ButtonsOptions,
                    default: ButtonsOptions[1] ? ButtonsOptions[1].key : undefined,
                    canShow: (data: NCheckoutAccountNewCms.IBlock) => data?.type !== 'separator'
                },
                <NICmsContentField.IField>{
                    key: '_sectionAdditional',
                    type: NICmsContentField.EFieldType.label,
                    title: 'Additional',
                    canShow: (data: NCheckoutAccountNewCms.IBlock) => data?.type !== 'separator'
                },
                <NICmsContentField.IToggleField>{
                    key: 'enabled',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Enabled',
                    default: true
                },
                <NICmsContentField.IChoiceField>{
                    key: 'backgroundColor',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Background Color',
                    options: BgColorOptions,
                    previewMode: 'color',
                    canShow: (data: NCheckoutAccountNewCms.IBlock) => data?.type !== 'separator'
                }
            ],
            default: [
                {
                    type: 'guest_checkout',
                    title: 'Checkout as a guest',
                    copy: 'Click continue with guest checkout to continue',
                    ctaText: 'Continue with guest checkout',
                    selectedTitle: 'Guest checkout',
                    selectedCopy:
                        'Enter your details to complete your purchase. After checkout, create an account to receive a welcome treat, enjoy early access to sales and new collections and more.',
                    selectedCtaText: 'Continue with guest checkout',
                    backgroundColor: 'bg-col-5'
                },
                {
                    type: 'sign_in',
                    title: 'Already have an account?',
                    copy: 'Sign in to checkout faster',
                    ctaText: 'Sign into your account',
                    selectedTitle: 'Already have an account?',
                    selectedCopy: 'Enter your email and password to sign in to your account.',
                    selectedCtaText: 'Sign into your account',
                    enabled: true,
                    backgroundColor: 'bg-col-5'
                },
                { type: 'separator', enabled: true },
                {
                    type: 'create_account',
                    title: 'Create an account',
                    copy: 'Create an account and receive a welcome treat when you join. Plus, enjoy early access to sales and new collections, quicker checkout, and more.',
                    ctaText: 'Create an account',
                    selectedTitle: 'Already have an account?',
                    selectedCopy:
                        'Create an account and receive a welcome treat when you join. Plus, enjoy early access to sales and new collections, quicker checkout, and more.',
                    selectedCtaText: 'Create an account',
                    enabled: true,
                    backgroundColor: 'bg-col-51'
                }
            ]
        },
        <NICmsContentField.IField>{
            key: '_sectionAdditional',
            type: NICmsContentField.EFieldType.label,
            title: 'Additional'
        },
        <NICmsContentField.ITextField>{
            key: 'termsAndConditions',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Terms and Conditions',
            default: 'I agree to the Terms & Conditions'
        }
    ]
};
